import Cancel from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { ValidationErrors } from 'fluentvalidation-ts';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Loader } from '../../../../Common/Components/Loader';
import { PaperWrapper } from '../../Common/CommonComponents';
import CustomButton from '../../../../Common/Components/Controls/InputControls';
import { generateAlphaNumericPassword } from '../../SqlServer/Common/util';
import { showToast } from '../../../../lib/toast';

import { customerService } from '../CustomerService';
import { InputClaimTypes, NewCustomer } from '../Model/CustomerModel';
import { EnvOption, EnvironmentOptionDto } from '../Model/CustomerProps';
import CustomerForm from './CustomerForm';

export default function CreateCustomerPage() {
  const { t } = useTranslation('customer');
  const history = useHistory();

  const [groupNames, setGroupNames] = useState<string[]>([]);
  const [envOptions, setEnvOptions] = useState<EnvOption[]>([]);
  const [customer, setCustomer] = useState<NewCustomer>({});
  const [loading, setLoading] = useState(0);
  const [customerErrors, setCustomerErrors] = useState<
    ValidationErrors<NewCustomer>
  >({});

  useEffect(() => {
    initData();
  }, []);

  /* -------------------- Data Loading -------------------- */

  const initData = async () => {
    await Promise.all([
      loadGroupNames(),
      loadEnvOptions(),
      loadDefaultValues(),
    ]);
  };

  const loadGroupNames = async () => {
    try {
      setLoading((c) => c + 1);
      const res = await customerService.getGroupNames();
      setGroupNames(res.Items);
    } catch (error) {
      showToast({
        title: t('validation.common_field'),
        description: error instanceof Error ? error.message : String(error),
        type: 'error',
      });
    } finally {
      setLoading((c) => c - 1);
    }
  };

  const loadEnvOptions = async () => {
    try {
      setLoading((c) => c + 1);
      const res = await customerService.getEnvironmentOptions();
      setEnvOptions(
        res.map((dto: EnvironmentOptionDto) => ({
          value: dto.Value,
          name: dto.Name,
        })),
      );
    } catch (error) {
      showToast({
        title: t('validation.common_field'),
        description: error instanceof Error ? error.message : String(error),
        type: 'error',
      });
    } finally {
      setLoading((c) => c - 1);
    }
  };

  const loadDefaultValues = async () => {
    try {
      setLoading((c) => c + 1);
      setCustomer({
        AdminUserPassword: generateAlphaNumericPassword(10),
      });
    } catch (error) {
      showToast({
        title: t('validation.common_field'),
        description: error instanceof Error ? error.message : String(error),
        type: 'error',
      });
    } finally {
      setLoading((c) => c - 1);
    }
  };

  /* -------------------- Handlers -------------------- */

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      setCustomer((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'IdentityProviders') {
      setCustomer((prev) => ({
        ...prev,
        IdentityProviders: value,
        InputClaimType: InputClaimTypes[value],
      }));
    } else {
      setCustomer((prev) => ({ ...prev, [name]: value }));
    }
  };

  const updateAdminUserPassword = (password: string) => {
    setCustomer((prev) => ({ ...prev, AdminUserPassword: password }));
  };

  /* -------------------- Validation -------------------- */

  const validateAuthenticationTypes = (): boolean => {
    if (!customer.EnableProArcLogin && !customer.ExternalIdentityProvider) {
      showToast({ title: t('validation.auth_type'), type: 'error' });
      return false;
    }
    return true;
  };

  const validateCustomerSection = (): boolean => {
    if (
      customerErrors.Subdomain ||
      customerErrors.CustomerName ||
      customerErrors.ContactPersons ||
      customerErrors.GroupName ||
      customerErrors.AdminUserName ||
      customerErrors.AdminUser
    ) {
      showToast({ title: t('validation.common_field'), type: 'error' });
      return false;
    }
    return true;
  };

  const validateIdentityProviderSection = (): boolean => {
    if (
      customer.ExternalIdentityProvider &&
      (customerErrors.AzureADAppClientID ||
        customerErrors.WSFederationUrl ||
        customerErrors.IdentityProviders ||
        customerErrors.InputClaimType ||
        customerErrors.AuthProviderName)
    ) {
      showToast({ title: t('validation.common_field'), type: 'error' });
      return false;
    }
    return true;
  };

  /* -------------------- Save -------------------- */

  const createCustomer = async () => {
    try {
      setLoading((c) => c + 1);
      await customerService.CreateCustomer(customer);
      showToast({ title: t('create_success'), type: 'success' });
      history.push('/customer');
    } catch (error) {
      showToast({
        title: t('validation.common_field'),
        description: error instanceof Error ? error.message : String(error),
        type: 'error',
      });
    } finally {
      setLoading((c) => c - 1);
    }
  };

  const handleSave = () => {
    if (
      validateAuthenticationTypes() &&
      validateCustomerSection() &&
      validateIdentityProviderSection()
    ) {
      createCustomer();
    }
  };

  /* -------------------- Render -------------------- */

  return (
    <PaperWrapper>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Create New Customer
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Loader loading={loading > 0} />

      <CustomerForm
        groupNames={groupNames}
        envOptions={envOptions}
        onFieldChange={handleChange}
        customer={customer}
        errors={customerErrors}
        setErrors={setCustomerErrors}
        ops="Create"
        updateUserAdminPassword={updateAdminUserPassword}
      />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Grid
          container
          spacing={2}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid item xs={12} sm="auto">
            <CustomButton
              onClick={handleSave}
              startIcon={<SaveIcon />}
              label="Save"
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <CustomButton
              onClick={() => history.push('/customer')}
              startIcon={<Cancel />}
              label="Cancel"
            />
          </Grid>
        </Grid>
      </Box>
    </PaperWrapper>
  );
}
